import { Document, mongo } from 'mongoose'
import {
  Dictionary,
  Installer,
  StreetAddress,
  LoggedUpload,
  LoanDocument,
  SolarDoc,
  CitizenshipValue,
  ITenant,
  Org,
  Individual,
  SIndividual,
  ApplicantRace,
  IncomeDocument,
  Incentive,
  FundReleaseRequest,
  Consent,
  YesNoEmpty,
  AmortizationSchedule,
  AccessLevel,
  CriteriaEvaluationResult,
  EquifaxDitReportDetail,
  Adder,
  EquipmentType,
  PrequalResult,
  Agents,
  EnvelopeRecord,
  EnvelopeKind,
  ClosingCostsDetail
} from './index'
import { StatusData } from './status-data'
import { PreSubmitStatus, PreSubmitUtils } from './presubmit-status'
import { Applicant } from './applicant'

export type StatusTokenPayload = {
  id: string
  uploadType?: string
  role?: 'applicant' | 'installer'
}

export type AirtableUserMeta = {
  id?: string
  email?: string
  name?: string
}

export type StepDocStatus = {
  requiredDocsCount: number
  approvedDocsCount: number
  docsNeedSupportActionCount: number
  docsNeedInstallerActionCount: number
  allDocsUploadedForStep: boolean
  allItemsComplete: boolean
}

export type ProjectType = 'residential' | 'commercial' | 'non-profit'
export type InstallerDocMap = Dictionary<Dictionary<LoanDocument>>
export type DitDecision = 'Approve' | 'Deny' | 'Review' | 'Error'

export enum FundingSchedule {
  FiftyFifty = "50_50",
  TwentyEighty = "20_80",
  OneHundred = "100",
}

export const FundingScheduleLabels: Record<FundingSchedule, string> = {
  [FundingSchedule.FiftyFifty]: "50% at M1 / 50% at M2",
  [FundingSchedule.TwentyEighty]: "20% at M1 / 80% at M2",
  [FundingSchedule.OneHundred]: "100% at final funding"
}

export enum FundReleaseType {
  RoofFundingRelease = "Roof Funding Release",
  InitialFundingRelease = "Initial Funding Release",
  FinalFundingRelease = "Final Funding Release",
  FullFundingRelease = "Full Funding Release"
}

export enum InstallerPatchableFields {
  adders,
  teslaPowerwallQty,
  storageQty,
  systemSizeKW,
  storageCapacity,
  ppWForPV,
  ppwForStorage,
  solarCost,
}

// not known if this is comprehensive list
export type Decision = "Approved-Pending Signed Contract" |
  "Approved" |
  "Booked (Solar + Roof)" |
  "Booked (Solar Only)" |
  "Canceled" |
  "Change Order Pending" |
  "Declined" |
  "Fully Funded" |
  "Loan Docs Requested" |
  "Partially Funded" |
  "Pending Phase 1 Funding" |
  "Pending Phase 2 Funding" |
  "Pending Roof Funding" |
  "Pending" |
  "Roof Funded" |
  "Withdrawn" |
  null


export type Trustee = {
  name: string,
  email: string,
  phone: string,
  address: StreetAddress,
}

export interface BaseLoanApplication extends Applicant {
  _id: string
  address: string
  airtableId: string
  appId?: number
  approvalDate?: Date
  assignee?: AirtableUserMeta
  applicationReceived: Date
  authorizedInstallers?: string[]
  avgMonthlyUtilityBill?: number
  estAvgMonthlyUtilityBillAfterSolar?: number
  customerName?: string
  businessName?: string
  projectType?: ProjectType
  principals?: Partial<Individual>[]
  incentive?: Incentive
  employerName: string
  occupation: string
  statedGrossAnnualIncome?: string // "$100,000.00"
  sourceLoanApplicationId?: typeof mongo.ObjectId
  loanNumber: string
  loanAccountNumber: string

  id: string
  installersStatus: string
  email: string
  loanDurationMonths: number
  loanAmount: number
  loanModFee?: number
  solarCost?: number
  systemCost?: number
  otherCost?: number
  systemSizeKW?: number
  ppWForPV?: number
  ppWForStorage?: number
  interest: number
  interestOnlyMonths?: number
  installerEmail?: string[]
  installerID: string
  source?: {
    kind: 'web' | 'api'
    integrationId: string
    key: string
  }
  branchId: string
  campaign: string
  decision?: string
  uploads?: LoggedUpload[]
  userAddress?: StreetAddress
  citizenship: CitizenshipValue
  race?: ApplicantRace
  statedHouseholdIncome?: string // "$100,000.00"
  coBorrowerAddress?: StreetAddress
  trustees?: Trustee[]

  // there's a bug where includeCoBorrower gets incorrectly set to 'no' despite being joint app;
  // accordingly includeCoBorrower considered unreliable except during initial eligibility 
  // check. Strongly prefer to use isJointApp instead.
  includeCoBorrower?: YesNoEmpty
  isJointApp: boolean
  isCoborrowerSpouse?: YesNoEmpty
  installationAddress?: StreetAddress
  aaNoticeGiven?: string
  nTPGiven?: string
  propertyAppraiserReviewed?: string,
  docPrepQueueDate?: string,
  contractReceived?: string,
  installedAtDifferentLocation?: YesNoEmpty
  incomeDocType?: 'paystubs' | 'W-2' | 'taxreturn'
  incomeVerificationDocuments?: SolarDoc[]
  useAutoDebit?: YesNoEmpty
  autoDebitAccountNo?: string
  autoDebitRoutingNo?: string
  autoDebitInstitution?: string
  agreesToTermsAndConditions?: Consent
  agreesToProceedWithHardCreditInquiry?: Consent
  agreesToWarningAgainstUsingOtherFinancing?: Consent
  agreesTo6MonthNoPaymentPeriod?: Consent
  agreesToProceedWithPrequal?: Consent
  agreesToContractDetails?: Consent
  attestConsumerResourceUnderstanding?: Consent
  stateInstalled?: string
  propertyHeldInTrust?: YesNoEmpty
  previousEmployment?: string
  yearsAtEmployer?: number
  fundingSchedule: FundingSchedule
  fundReleasesRequested?: FundReleaseRequest[]
  finalFundReleaseAmount?: number
  equipmentType?: EquipmentType
  teslaPowerwallQty?: number
  storageQty?: number
  storageCapacity?: number
  // installWillCompleteIn30Days: YesNoEmpty

  coFirstName: string
  coMiddleName: string
  coLastName: string
  coSsn: string
  coPhone: string
  coEmail: string
  coBirthDate: string
  coDriversLicenseNo: string
  coDriversLicenseIssueDate: string
  coDriversLicenseIssuingState: string
  coDriversLicenseExpirationDate: string
  coCitizenship: CitizenshipValue
  coRace?: ApplicantRace
  coEmployerName: string
  coOccupation: string
  coYearsAtEmployer: number
  coPreviousEmployment: string
  coIncomeDocType?: 'paystubs' | 'W-2' | 'taxreturn'
  coIncomeVerificationDocuments?: SolarDoc[]
  coStatedGrossAnnualIncome: string // "$100,000.00" used for prequal
  coStatedHouseholdIncome?: string // "$100,000.00" reporting purposes only, not used for prequal
  coHasMortgage?: YesNoEmpty
  lookup?: string

  abrigoProposedLoanId?: number
  abrigoCoborrowerLoanRoleId?: number
  abrigoLoanApplicationId?: number
  abrigoWorkflowId?: number
  error: string

  isLocked?: boolean
  lockedTimestamp?: Date
  maxAllowableMonthlyPayment?: number
  prequalResult?: PrequalResult
  prequalTimestamp?: Date
  prequalResultCombined?: PrequalResult
  prequalResultCombinedTimestamp?: Date
  prequalError?: any
  prescreened?: boolean
  declinedToAddCoborrowerDate?: Date
  ditDecision?: DitDecision
  coDitDecision?: DitDecision
  hasOfacAlerts?: boolean
  ofacAlerts?: EquifaxDitReportDetail[]
  coOfacAlerts?: EquifaxDitReportDetail[]
  ditError?: string
  preSubmitStatus?: PreSubmitStatus
  slackLink?: string
  submissionResponse?: any,
  submittedFromIp?: string
  submittedFromTzOffset?: number
  submittedTimestamp?: Date
  tenant?: ITenant
  notesVisibleToInstallers?: string
  // consumerRequestedContractChanges?: {
  //   message: string
  //   timestamp: Date
  //   resolved?: boolean
  // }[]

  // should be duplicates of branchId, installerID, and campaign respectively
  utm_medium: string
  utm_source: string
  utm_campaign: string

  rim: string
  installerDocs?: InstallerDocMap
  envelopes?: EnvelopeRecord[]
  pendingBookingDate?: string // from airtable; important; tells us loan docs are signed
  loanDocsSignedDate?: Date // future when we have from docusign
  loanDocsIssuedDate?: string // from airtable; will be set initially when sending docs via docusign
  lastStatusChange?: Date
  lastModified?: Date
  lastModifiedBy?: AirtableUserMeta
  createdDate?: Date
  updatedDate?: Date
  preapprovalLetter?: {
    doc: SolarDoc
    contentMD5: string
    agreement?: Consent
  }
  installerProbationaryStatus?: string[]
  fundingBlock?: string

  // V1 new fields
  productId: string
  projectUrl?: string
  loanProposalId: string
  loanRequestId: string
  monthlyAverageEnergy: number
  adders: Adder[]

  // Loan Application Phase Airtable Fields
  contractNotes: string,
  loanDocumentsSentNotes: string,
  loanDocumentsSignedNotes: string,
  initialBankReviewNotes: string,
  homeOwnershipVerificationNotes: string,
  bankFinalReviewNotes: string,

  // Loan Funding Phase Airtable Fields
  roofPermittingComments: string,
  passedRoofInspectionComments: string,
  roofLienWaiverFormComments: string,
  roofFundReleaseRequestComments: string,
  roofFundingDisbursedComments: string,
  roofFundingDate: string,
  constructionPermitComments: string,
  engineeringPlansComments: string,
  initialFundReleaseRequestComments: string,
  initialFundingDisbursedComments: string,
  partialFundingDate: string,
  proofOfPassedInspectionComments: string,
  proofOfPermissionToOperateComments: string,
  contractorLienWaiverFormComments: string,
  finalFundingReleaseRequestComments: string,
  finalFundingDisbursedComments: string,
  fullFundingDate: string,
  fullFundingReleaseRequestComments: string,

  addOrUpdatePrincipal(p: Individual): SIndividual
  getReferralToken: () => string
  getStatusData: (access?: AccessLevel) => StatusData
  getStreet: () => string
  getDoc: (id: string) => LoanDocument | undefined
  docForSlot: (slot: string) => LoanDocument | undefined
  getInterestOnlyMonths: () => number
  getAmortization: (asOf?: Date) => AmortizationSchedule
  getRoofCost: () => number
  getFundReleaseAmounts: () => Record<string, number>
  getStepDocStatus: () => StepDocStatus
  isResidential: () => boolean
  isDeclined: () => boolean
  isPrequalified: () => YesNoEmpty
  processDecline: () => Promise<void>
  lastUploadForStatus(status: string): LoggedUpload | undefined
  lastUploadForType(uploadType: string): LoggedUpload | undefined
  logUpload(
    uploadType: string,
    documents: LoanDocument[],
    uploadedBy?: string,
    notes?: string
  ): Promise<void>
  projectAddress: () => string
  estimatedPayment: () => number
  estimatedApr: () => number
  estimatedClosingCosts: () => number
  estimatedClosingCostsDetail: () => ClosingCostsDetail
  handleAgentOutput: (run: Agents.IWorkflowRun) => void
  setMaxAllowableMonthlyPayment: (evaluations: CriteriaEvaluationResult[]) => void
  halfCostsMinusRoof: () => [number, number]
  unsetSensitiveData: () => void
  renderableData: (t?: ITenant, org?: Org) => Record<string, string>
  hasMinPrequalInfo: () => boolean
  hasMinCoborrowerPrequalInfo: () => boolean
  validateConsumerApplication_incomplete: () => string[]
  requiresSpouse: () => boolean
  requiresSpouseSignature: () => boolean
  spouseName: () => string
  primaryBorrowerName: () => string
  coborrowerName: () => string
}

export type LoanApplication = BaseLoanApplication & Document

// servier loan application
export type SLoanApplication = LoanApplication & {
  _id: typeof mongo.ObjectId
  creditReports: typeof mongo.ObjectId[]
  referralSubmittedBy: typeof mongo.ObjectId | string | Installer
  toMaskedObject(): Partial<SLoanApplication>
  updateConsumerAssignableFields(o: Partial<SLoanApplication>): void
  getPresubmitErrors(): string[]
  getReadyForDocsErrors(): string[]
  getLookupHash(): string
  getCollateralDescription(): string
  addEnvelope(envelope: EnvelopeRecord): void
  updateEnvelope(envelopeId: string, status: string): void
  setAllEnvelopesVoided(kind: EnvelopeKind): Promise<SLoanApplication>
  shouldAutoSendLoanDocs(): boolean
  hasContractBeenUploaded(): boolean
  hasApprovedAgreementForFundReleaseType(fundReleaseType: string): boolean
  getApprovedFundReleaseRequest(
    fundReleaseType: string
  ): FundReleaseRequest
}

// client loan application
export type CLoanApplication = LoanApplication & {
  consumerId: string
  _id: string
}

export type SLoanAppField = keyof SLoanApplication
export type CLoanAppField = keyof CLoanApplication
export type LoanAppField = keyof LoanApplication

export const getIncomeDocErrorsMultiUpload = (
  lapp: LoanApplication,
  docsArray: IncomeDocument[],
  coDocsArray: IncomeDocument[]
): string[] => {
  const errors: string[] = []

  if (PreSubmitUtils.isAfter('Co-Borrower', lapp.preSubmitStatus)) {
    if (docsArray) {
      docsArray.forEach((docSet) => {
        if (docSet.docType === 'paystubs' && docSet.documents?.length !== 2) {
          errors.push(`Exactly two paystub documents required for primary borrower`)
        } else if (['W-2', 'taxreturn'].includes(docSet.docType) && docSet.documents?.length !== 1) {
          errors.push(`Exactly one W-2 or one tax return document required for primary borrower`)
        }
      })
    }

    if (lapp.isJointApp && coDocsArray) {
      coDocsArray.forEach((docSet) => {
        if (docSet.docType === 'paystubs' && docSet.documents?.length !== 2) {
          errors.push(`Exactly two paystub documents required for co-borrower`)
        } else if (['W-2', 'taxreturn'].includes(docSet.docType) && docSet.documents?.length !== 1) {
          errors.push(`Exactly one W-2 or one tax return document required for co-borrower`)
        }
      })
    }
  }
  return errors
}

export const getFundReleaseDetails = (
  type: string,
  installer: string,
  fundingSchedule: FundingSchedule
) => {
  switch (type?.toLowerCase()) {
    case 'roof funding release':
      return {
        title: 'Roof Funding Release',
        description: `Your installer, ${installer}, has issued a Roof Funding Release Request as part of your solar installation project. This step is required to permit the bank to release these funds (representing 100% of the roof project cost) on your behalf to your installer.`,
        requirements: [
          {
            title: '100% Completion of Roof Work:',
            detail: 'The roof must be fully repaired or replaced as per the project specifications.'
          },
          {
            title: 'Passed Inspection:',
            detail: 'The roof must pass all required inspections by the Authority Having Jurisdiction (AHJ). If the AHJ does not conduct inspections, the installer must provide a Completion Certificate, signed by the contractor, confirming the roof work is complete and meets all relevant standards.'
          }
        ]
      }
    case 'initial funding release':
      return {
        title: 'Initial Funding Release',
        description: `Your installer, ${installer}, has issued an Initial Funding Release Request 
          as part of your solar installation project. This step is required to permit the bank to release these funds 
          (representing the initial ${fundingSchedule === FundingSchedule.FiftyFifty ? '50%' : '20%'} of the solar project cost) on your behalf to your installer.`,
        requirements: [
          {
            title: 'Solar Permit:',
            detail: 'The installer must obtain the required permit in compliance with state regulations. If a permit is not required in your area, confirm that you have an understanding of the project details and timeline.'
          }
        ]
      }
    case 'final funding release':
      return {
        title: 'Final Funding Release',
        description: `Your installer, ${installer}, has issued a Final Funding Release Request as part 
          of your solar installation project. This step is required to permit the bank to release these funds (representing 
          the final ${fundingSchedule === FundingSchedule.FiftyFifty ? '50%' : '80%'} of the solar project cost) on your behalf to your installer.`,
        requirements: [
          {
            title: 'Solar Panel Installation:',
            detail: 'All solar panels are installed as expected.'
          },
          {
            title: 'Passed Inspection:',
            detail: 'The project must have passed all relevant inspections by the Authority Having Jurisdiction (AHJ).'
          },
          {
            title: 'Completion of Solar-Related Work:',
            detail: 'All solar-related work, including electrical connections and system set-up, must be completed as per the project specifications.'
          }
        ]
      }
    case 'full funding release':
      return {
        title: 'Full Funding Release',
        description: `Your installer, ${installer}, has issued a Full Funding Release Request as part of your solar installation project. This step is required to permit the bank to release these funds (representing 100% of the total project cost) on your behalf to your installer.`,
        requirements: [
          {
            title: 'Completed Permitting:',
            detail: 'All relevant permits have been obtained in compliance with all applicable state and local regulations.'
          },
          {
            title: 'Solar Panel Installation:',
            detail: 'All solar panels are installed as expected.'
          },
          {
            title: 'Passed Inspection:',
            detail: 'The project must have passed all relevant inspections by the Authority Having Jurisdiction (AHJ).'
          },
          {
            title: 'Completion of Solar-Related Work:',
            detail: 'All solar-related work, including electrical connections and system set-up, must be completed as per the project specifications.'
          },
          {
            title: 'Completion of Non-Solar Work:',
            detail: 'All non-solar work agreed upon in the solar contract must also be completed.'
          }
        ]
      }
    default: null
  }
}

export const getCurrentFundRelease = (
  loanApplication: StatusData,
  fundReleaseType: string
) => {
  if (!loanApplication.fundReleasesRequested?.length || !fundReleaseType) return null
  return loanApplication.fundReleasesRequested.find(release => release.type === fundReleaseType)
}

// This helper function ensures that a fund release request is counted 
// as a required document when calculating the total number of documents 
// needed for an step.

export const getRequireDocsForStep = (
  lapp: StatusData,
  fundReleaseTypeForStep: string,
) => {
  const currentFundRelease = getCurrentFundRelease(lapp, fundReleaseTypeForStep)
  const fundReleaseRequiredCount = fundReleaseTypeForStep ? 1 : 0
  const fundReleaseCompletedCount = currentFundRelease?.agreementSigned.agrees ? 1 : 0
  let docsNeedActionCount = lapp.docsNeedInstallerActionCount
    + fundReleaseRequiredCount - fundReleaseCompletedCount
  
  // don't require docs until the application has been prequalified OR received
  if (!lapp.applicationReceived && lapp.isPrequalified === undefined) {
    docsNeedActionCount = 0
  }

  const requiredDocumentsCount = lapp.requiredDocuments
    ? Object.keys(lapp.requiredDocuments).length
    : 0

  return {
    fundReleaseRequiredCount,
    fundReleaseCompletedCount,
    requiredDocsCount: requiredDocumentsCount + fundReleaseRequiredCount,
    docsNeedActionCount
  }
}

export const getPayableAmounts = (
  fundingSchedule: FundingSchedule | undefined,
  roofFundingAmount: number,
  initialFundingAmount: number,
  finalFundingAmount: number,
  fullFundingAmount: number
): {
    payableAmount1: number,
    payableAmount2?: number,
    payableAmount3?: number
  } => {

  let payableAmount1
  let payableAmount2
  let payableAmount3

  const schedule = fundingSchedule || FundingSchedule.FiftyFifty

  if (roofFundingAmount > 0) {
    switch (schedule) {
      case FundingSchedule.OneHundred:
        payableAmount1 = fullFundingAmount
        break
      case FundingSchedule.FiftyFifty:
      case FundingSchedule.TwentyEighty:
        payableAmount1 = roofFundingAmount
        payableAmount2 = initialFundingAmount
        payableAmount3 = finalFundingAmount
        break
    }
  } else {
    switch (schedule) {
      case FundingSchedule.FiftyFifty:
      case FundingSchedule.TwentyEighty:
        payableAmount1 = initialFundingAmount
        payableAmount2 = finalFundingAmount
        break
      case FundingSchedule.OneHundred:
        payableAmount1 = fullFundingAmount
        break
    }
  }

  return {
    payableAmount1,
    payableAmount2,
    payableAmount3
  }
}
